import axios from "axios";
import { auth } from "../libs/firebase";
import { TPropertyType } from "../typos/properties";

export type TOrder = "asc" | "desc";

export type TFilesObj = {
  name: string;
  content: ArrayBuffer;
  contentType: string;
};

export function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

export function getComparator<Key extends keyof any>(
  order: TOrder,
  orderBy: Key,
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string },
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

export function stableSort<T>(
  array: readonly T[],
  comparator: (a: T, b: T) => number,
) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

export function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const binary = Array.from(new Uint8Array(buffer))
    .map((byte) => String.fromCharCode(byte))
    .join("");
  return btoa(binary);
}

export async function getFilesPreview(files: FileList): Promise<TFilesObj[]> {
  const filesObj: TFilesObj[] = [];

  if (files && files.length) {
    const promises = [];

    for (let i = 0; i < files.length; i += 1) {
      const promise = new Promise((resolve, reject) => {
        const FR = new FileReader();
        const f = files[i];

        FR.onloadend = () => {
          if (FR.result) {
            const arrayBuffer = FR.result as ArrayBuffer;

            const fileName = f.name;

            const obj = {
              name: fileName,
              content: arrayBuffer,
              contentType: f.type,
            };

            filesObj.push(obj);

            resolve(obj);
          } else {
            reject();
          }
        };

        FR.readAsArrayBuffer(f);
      });

      promises.push(promise);
    }

    await Promise.all(promises);
  }

  return filesObj;
}

export function convertFileContentToBase64(
  content: ArrayBuffer,
  contentType: string,
): string {
  if (content) {
    const fileBase64 = arrayBufferToBase64(content);
    return `data:${contentType};base64,${fileBase64}`;
  }

  return "";
}

export async function getUserData() {
  const token = await auth.currentUser?.getIdToken();

  if (!token) throw new Error("Invalid token");

  return axios.get(`${process.env.REACT_APP_API_URL}/v1/users/me`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export function formatPropertyType({
  propertyType,
  isResidentialComplex = false,
  floorsCount = 0,
}: {
  propertyType: TPropertyType;
  isResidentialComplex?: boolean;
  floorsCount?: number;
}) {
  if (propertyType === "APARTMENT") {
    if (floorsCount > 1) return "Apartamento Penthouse";
    return "Apartamento";
  }

  if (propertyType === "HOUSE") {
    if (isResidentialComplex) return "Casa en conjunto cerrado";
    return "Casa";
  }

  if (propertyType === "BUILDING") return "Edifício";
  if (propertyType === "LOT") return "Lote";
  if (propertyType === "WAREHOUSE") return "Bodega";

  return "Local comercial";
}
